import React from 'react';
import { AlertTriangle, AlertCircle, FlaskConical } from 'lucide-react';
import type { Chemical } from '../lib/chemicalService';

interface ChemicalWarningProps {
  chemicals: {
    haramChemicals: Chemical[];
    mushboohChemicals: Chemical[];
    allChemicals: Chemical[];
  };
}

export const ChemicalWarning: React.FC<ChemicalWarningProps> = ({ chemicals }) => {
  if (chemicals.allChemicals.length === 0) return null;

  return (
    <div className="space-y-4">
      {chemicals.haramChemicals.length > 0 && (
        <div className="bg-red-50 border border-red-100 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-red-700 font-medium">Haram Chemicals Detected</h4>
              <div className="mt-2 space-y-3">
                {chemicals.haramChemicals.map(chemical => (
                  <div key={chemical.pubchem_cid} className="text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="font-medium text-red-700">{chemical.name}</span>
                        {chemical.molecular_formula && (
                          <span className="text-red-600 ml-2">({chemical.molecular_formula})</span>
                        )}
                      </div>
                      <a
                        href={`https://pubchem.ncbi.nlm.nih.gov/compound/${chemical.pubchem_cid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-600 hover:text-red-700 shrink-0"
                      >
                        View on PubChem →
                      </a>
                    </div>
                    {chemical.source && (
                      <p className="mt-1 text-red-500">Source: {chemical.source}</p>
                    )}
                    {chemical.description && (
                      <p className="mt-1 text-red-600">{chemical.description}</p>
                    )}
                    {chemical.safety_notes && (
                      <p className="mt-1 text-red-500 flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        {chemical.safety_notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {chemicals.mushboohChemicals.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-yellow-700 font-medium">Questionable Chemicals Found</h4>
              <div className="mt-2 space-y-3">
                {chemicals.mushboohChemicals.map(chemical => (
                  <div key={chemical.pubchem_cid} className="text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="font-medium text-yellow-700">{chemical.name}</span>
                        {chemical.molecular_formula && (
                          <span className="text-yellow-600 ml-2">({chemical.molecular_formula})</span>
                        )}
                      </div>
                      <a
                        href={`https://pubchem.ncbi.nlm.nih.gov/compound/${chemical.pubchem_cid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-yellow-600 hover:text-yellow-700 shrink-0"
                      >
                        View on PubChem →
                      </a>
                    </div>
                    {chemical.source && (
                      <p className="mt-1 text-yellow-500">Source: {chemical.source}</p>
                    )}
                    {chemical.description && (
                      <p className="mt-1 text-yellow-600">{chemical.description}</p>
                    )}
                    {chemical.safety_notes && (
                      <p className="mt-1 text-yellow-500 flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        {chemical.safety_notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-3 text-sm text-yellow-600">
                These chemicals require verification from the manufacturer or halal certification.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="text-sm text-gray-500">
        <div className="flex items-center gap-1.5">
          <FlaskConical className="w-4 h-4" />
          <span>
            All detected chemicals: {chemicals.allChemicals.map(c => c.name).join(', ')}
          </span>
        </div>
      </div>
    </div>
  );
};